import React from 'react';
import { Box, Typography, Button, Container, useTheme, keyframes, alpha } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Home as HomeIcon, SearchOff } from '@mui/icons-material';
import { glassStyles } from '../../tokens/designTokens';

// Subtle floating animation for the 404 text
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

// Glow pulse animation
const pulse = keyframes`
  0% { text-shadow: 0 0 20px rgba(138, 43, 226, 0.4), 0 0 40px rgba(138, 43, 226, 0.2); }
  50% { text-shadow: 0 0 30px rgba(138, 43, 226, 0.7), 0 0 60px rgba(138, 43, 226, 0.4); }
  100% { text-shadow: 0 0 20px rgba(138, 43, 226, 0.4), 0 0 40px rgba(138, 43, 226, 0.2); }
`;

export const NotFoundScreen: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.default',
        // Dynamic background gradients
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '50vw',
          height: '50vw',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%)`,
          filter: 'blur(60px)',
          animation: `${float} 10s ease-in-out infinite alternate`,
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: '60vw',
          height: '60vw',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary?.main || '#9c27b0', 0.1)} 0%, transparent 60%)`,
          filter: 'blur(80px)',
          animation: `${float} 15s ease-in-out infinite alternate-reverse`,
          zIndex: 0,
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <Box 
          sx={{ 
            ...glassStyles.card,
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 8px 32px 0 ${alpha('#000', 0.2)}, inset 0 0 0 1px ${alpha('#fff', 0.05)}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Main 404 Text */}
          <Typography 
            variant="h1" 
            sx={{ 
              fontSize: { xs: '6rem', md: '8rem' },
              fontWeight: 900,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || '#9c27b0'})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${float} 6s ease-in-out infinite`,
              mb: -2,
            }}
          >
            4
            <SearchOff sx={{ fontSize: 'inherit', color: theme.palette.primary.main, animation: `${pulse} 4s infinite` }} />
            4
          </Typography>

          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              letterSpacing: '-0.02em'
            }}
          >
            Lost in the cinematic vacuum string
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              mb: 2,
              maxWidth: '80%',
              lineHeight: 1.6
            }}
          >
            The page or movie you're looking for seems to have been cut from the final scene. Let's get you back to the main feature.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/', { replace: true })}
            sx={{
              borderRadius: 8,
              py: 1.5,
              px: { xs: 4, md: 6 },
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1.1rem',
              backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              '&:active': {
                transform: 'translateY(1px)',
              }
            }}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFoundScreen;
